import { Award, Users, Heart, Clock, MapPin, Phone, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f90ccf59-22af-4309-9fe3-b65d82f9d7de/artifacts/ssdhf0gw_IMG_1394.jpeg";
const PHONE_NUMBER = "+919971231812";

const milestones = [
  { year: '1992', title: 'Founded', description: 'Started as a small foam shop in Yusuf Sarai' },
  { year: '2000', title: 'Expansion', description: 'Added premium mattress brands to our collection' },
  { year: '2010', title: 'Growth', description: 'Became deals in partner for major brands' },
  { year: '2020', title: 'Digital', description: 'Expanded to online presence with IndiaMART' },
  { year: 'Today', title: '30+ Years', description: 'Serving thousands of happy customers' },
];

const stats = [
  { number: '30+', label: 'Years of Trust' },
  { number: '10,000+', label: 'Happy Customers' },
  { number: '150+', label: 'Products' },
  { number: '4.5', label: 'Google Rating' },
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page" className="bg-[#FDFBF7]">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-[#FDFBF7] via-[#F0FDFA] to-[#CCFBF1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-teal-700 mb-3">
                Our Story
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-800 mb-6">
                A Legacy of{' '}
                <span className="text-teal-600">Comfort & Trust</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                For over three decades, Aggarwal Foam Palace has been the go-to destination 
                for quality home furnishing in South Delhi. What started as a small family 
                shop in 1992 has grown into one of the most trusted names in mattresses 
                and bedding in the region.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={`tel:${PHONE_NUMBER}`}>
                  <Button 
                    size="lg"
                    className="rounded-full bg-teal-600 hover:bg-teal-700"
                    data-testid="about-call-btn"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Us
                  </Button>
                </a>
                <a 
                  href="https://maps.app.goo.gl/tLGnG1nKfKTFJZHaA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg"
                    variant="outline"
                    className="rounded-full border-teal-600 text-teal-700"
                    data-testid="about-visit-btn"
                  >
                    <MapPin className="w-5 h-5 mr-2" />
                    Visit Store
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={LOGO_URL}
                  alt="Aggarwal Foam Palace Logo"
                  className="w-full h-auto"
                  data-testid="about-logo-image"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-bold text-stone-800">Since 1992</p>
                    <p className="text-sm text-stone-500">Trusted for 30+ years</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-teal-600 mb-2">
                  {stat.number}
                </p>
                <p className="text-stone-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-teal-700 mb-3">
              What We Stand For
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800">
              Our Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Customer First',
                description: 'Every decision we make is centered around providing the best experience for our customers.',
              },
              {
                icon: Award,
                title: 'Quality Assured',
                description: 'We only stock products from brands we trust and have tested ourselves.',
              },
              {
                icon: Users,
                title: 'Family Values',
                description: 'Run by the Aggarwal family, we treat every customer like a member of our extended family.',
              },
            ].map((value, index) => (
              <div 
                key={index}
                data-testid={`value-card-${index}`}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-stone-100 text-center"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-stone-800 mb-3">{value.title}</h3>
                <p className="text-stone-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-widest uppercase text-teal-700 mb-3">
              Our Journey
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800">
              30+ Years of Excellence
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-teal-200 h-full" />
            
            <div className="space-y-8 md:space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  data-testid={`milestone-${index}`}
                  className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-sm inline-block">
                      <span className="text-teal-600 font-bold text-2xl">{milestone.year}</span>
                      <h3 className="font-bold text-stone-800 text-lg">{milestone.title}</h3>
                      <p className="text-stone-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-teal-600 rounded-full ring-4 ring-teal-100 z-10" />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-teal-200 font-medium mb-3">Leadership</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Under the guidance of Mr. Yoginder Aggarwal
                </h2>
                <p className="text-teal-100 mb-6 leading-relaxed">
                  With decades of experience in the home furnishing industry, Mr. Aggarwal 
                  has built a reputation for honest dealings and quality products. His 
                  personal involvement in guiding customers has made AFP the trusted name it is today.
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < 4 ? 'text-amber-400 fill-current' : 'text-amber-400 fill-current opacity-50'}`} />
                    ))}
                  </div>
                  <span className="text-teal-100">4.5/5 Customer Rating</span>
                </div>
              </div>
              <div className="text-center lg:text-right">
                <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <Clock className="w-12 h-12 text-white mx-auto mb-4" />
                  <p className="text-2xl font-bold">Visit Us Today</p>
                  <p className="text-teal-100">10:30 AM - 9:30 PM</p>
                  <p className="text-teal-100">Open All Days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
